import React, { useState, useEffect } from 'react';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Phone, 
  Clock, 
  Building2,
  Download,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { SkeletonMetricGrid, SkeletonChartGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { chartLabel } from '@/lib/recharts-format';

interface KPIData {
  total_calls: number;
  active_tenants: number;
  avg_call_duration: number;
  total_users: number;
  trend?: {
    calls: number;
    tenants: number;
    duration: number;
    users: number;
  };
}

interface TrendData {
  time_bucket: string;
  total_calls: number;
  inbound_calls: number;
  outbound_calls: number;
  internal_calls: number;
}

interface TopTenant {
  tenant_name: string;
  total_calls: number;
  answered_calls: number;
}

interface QualityMetric {
  tenant_name: string;
  asr_percentage: number;
  avg_mos: number;
  avg_jitter: number;
}

const COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b'];
const PERIOD_OPTIONS = [
  { value: '1h', label: 'Last Hour' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
];

export default function SuperAdminAnalytics() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('24h');
  const [selectedTenant, setSelectedTenant] = useState<string>('all');
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  
  const [kpiData, setKpiData] = useState<KPIData>({
    total_calls: 0,
    active_tenants: 0,
    avg_call_duration: 0,
    total_users: 0,
    trend: { calls: 0, tenants: 0, duration: 0, users: 0 }, // Initialize trend
  });
  
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [topTenants, setTopTenants] = useState<TopTenant[]>([]);
  const [callDistribution, setCallDistribution] = useState<Array<{ name: string; value: number }>>([]);
  const [qualityMetrics, setQualityMetrics] = useState<QualityMetric[]>([]);

  useEffect(() => {
    void loadTenants();
  }, []);

  useEffect(() => {
    void loadAnalytics();
  }, [period, selectedTenant]);

  const loadTenants = async () => {
    try {
      const response = await apiClient.get<{ tenants: Array<{ id: string; name: string }> }>('/master/tenants');
      if (response.success && response.data?.tenants) {
        setTenants(response.data.tenants);
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      setIsLoading(true);
      console.log('Loading analytics with period:', period, 'tenant:', selectedTenant);
      
      // Build query strings
      const trendParams = selectedTenant !== 'all' 
        ? `?period=${period}&tenant_id=${selectedTenant}` 
        : `?period=${period}`;
      
      // Load all data in parallel
      const [summaryResponse, trendResponse, topTenantsResponse, qualityResponse] = await Promise.all([
        apiClient.get<KPIData>(`/master/analytics/cross-tenant/summary?period=${period}`),
        apiClient.get<TrendData[]>(`/master/analytics/cross-tenant/calls-trend${trendParams}`),
        apiClient.get<TopTenant[]>(`/master/analytics/cross-tenant/top-tenants?period=${period}&limit=10`),
        apiClient.get<QualityMetric[]>(`/master/analytics/cross-tenant/quality-metrics?period=${period}`)
      ]);

      // Process KPI data
      const kpiResponseData = summaryResponse.success ? summaryResponse.data : null;
      if (kpiResponseData) {
        setKpiData({
          total_calls: kpiResponseData.total_calls || 0,
          active_tenants: kpiResponseData.active_tenants || 0,
          avg_call_duration: kpiResponseData.avg_call_duration || 0,
          total_users: kpiResponseData.total_users || 0,
          trend: kpiResponseData.trend || { calls: 0, tenants: 0, duration: 0, users: 0 },
        });
      }

      // Process trend data
      const newTrendData = trendResponse.success && Array.isArray(trendResponse.data) ? trendResponse.data : [];
      setTrendData(newTrendData);

      // Calculate call distribution from fresh trend data (not stale state)
      // Debug: log the trend data to verify structure
      console.log('Trend data received:', newTrendData);
      
      if (newTrendData.length > 0) {
        const totals = newTrendData.reduce((acc, item) => ({
          inbound: acc.inbound + (Number(item.inbound_calls) || 0),
          outbound: acc.outbound + (Number(item.outbound_calls) || 0),
          internal: acc.internal + (Number(item.internal_calls) || 0),
        }), { inbound: 0, outbound: 0, internal: 0 });

        console.log('Call distribution totals:', totals);
        
        const distribution = [
          { name: 'Inbound', value: totals.inbound },
          { name: 'Outbound', value: totals.outbound },
          { name: 'Internal', value: totals.internal },
        ].filter(item => item.value > 0);
        
        console.log('Final distribution:', distribution);
        setCallDistribution(distribution);
      } else {
        console.log('No trend data - setting empty distribution');
        setCallDistribution([]);
      }

      // Process top tenants
      if (topTenantsResponse.success && Array.isArray(topTenantsResponse.data)) {
        setTopTenants(topTenantsResponse.data);
      }

      // Process quality metrics
      if (qualityResponse.success && Array.isArray(qualityResponse.data)) {
        setQualityMetrics(qualityResponse.data);
      }

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load analytics';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatTimeBucket = (timestamp: string) => {
    const date = new Date(timestamp);
    if (period === '1h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (period === '24h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const exportToCSV = () => {
    const headers = ['Time', 'Total Calls', 'Inbound', 'Outbound', 'Internal'];
    const rows = trendData.map(item => [
      item.time_bucket,
      item.total_calls,
      item.inbound_calls,
      item.outbound_calls,
      item.internal_calls
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics-${period}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    toast({
      title: 'Export completato',
      description: `File CSV esportato con ${rows.length} righe`,
    });
  };

  const exportToJSON = () => {
    const exportData = {
      period,
      exported_at: new Date().toISOString(),
      kpis: kpiData,
      trend: trendData,
      top_tenants: topTenants,
      quality_metrics: qualityMetrics,
      call_distribution: callDistribution
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `analytics-${period}-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(link.href);

    toast({
      title: 'Export completato',
      description: 'File JSON esportato con tutti i dati analytics',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        
        <SkeletonMetricGrid cols={4} />
        
        <SkeletonChartGrid charts={3} height={300} />
        
        <SkeletonTable rows={10} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Analytics Dashboard
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Cross-tenant analytics and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => loadAnalytics()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
          <Button variant="outline" onClick={exportToJSON}>
            <Download className="mr-2 h-4 w-4" />
            JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <GlassCard>
        <GlassCardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Time Period</label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Tenant Filter</label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map(tenant => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </GlassCardContent>
      </GlassCard>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard className="border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50 to-white">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Total Calls</span>
              <Phone className="h-4 w-4 text-blue-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-3xl font-bold text-blue-900">
              {isLoading ? '...' : kpiData.total_calls?.toLocaleString() || 0}
            </div>
            {kpiData.trend?.calls !== undefined && kpiData.trend?.calls !== null && (
              <div className={`text-sm flex items-center gap-1 mt-1 ${(kpiData.trend?.calls || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(kpiData.trend?.calls || 0) >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                <span>{Math.abs(kpiData.trend?.calls || 0)}%</span>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="border-l-4 border-l-purple-500 bg-gradient-to-br from-purple-50 to-white">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Active Tenants</span>
              <Building2 className="h-4 w-4 text-purple-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-3xl font-bold text-purple-900">
              {isLoading ? '...' : kpiData.active_tenants || 0}
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-white">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Avg Duration</span>
              <Clock className="h-4 w-4 text-green-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-3xl font-bold text-green-900">
              {isLoading ? '...' : formatDuration(kpiData.avg_call_duration || 0)}
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard className="border-l-4 border-l-orange-500 bg-gradient-to-br from-orange-50 to-white">
          <GlassCardHeader className="pb-2">
            <GlassCardDescription className="flex items-center justify-between">
              <span>Total Users</span>
              <Users className="h-4 w-4 text-orange-600" />
            </GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="text-3xl font-bold text-orange-900">
              {isLoading ? '...' : kpiData.total_users || 0}
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls Trend Line Chart */}
        <GlassCard className="lg:col-span-2">
          <GlassCardHeader>
            <GlassCardTitle>Calls Trend Over Time</GlassCardTitle>
            <GlassCardDescription>Call volume by direction</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time_bucket" tickFormatter={formatTimeBucket} />
                <YAxis />
                <Tooltip labelFormatter={(label) => formatTimeBucket(chartLabel(label))} />
                <Legend />
                <Line type="monotone" dataKey="inbound_calls" stroke="#3b82f6" name="Inbound" strokeWidth={2} />
                <Line type="monotone" dataKey="outbound_calls" stroke="#8b5cf6" name="Outbound" strokeWidth={2} />
                <Line type="monotone" dataKey="internal_calls" stroke="#10b981" name="Internal" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>

        {/* Top Tenants Bar Chart */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Top 10 Tenants by Volume</GlassCardTitle>
            <GlassCardDescription>Highest call volumes</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topTenants} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="tenant_name" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="total_calls" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </GlassCardContent>
        </GlassCard>

        {/* Call Distribution Pie Chart */}
        <GlassCard>
          <GlassCardHeader>
            <GlassCardTitle>Call Distribution</GlassCardTitle>
            <GlassCardDescription>By call type</GlassCardDescription>
          </GlassCardHeader>
          <GlassCardContent>
            {callDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={callDistribution}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={(props: { name?: string; value?: number; percent?: number }) => `${props.name}: ${props.value} (${((props.percent ?? 0) * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {callDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(15, 23, 42, 0.9)', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground dark:text-muted-foreground/70">
                No call distribution data available for this period
              </div>
            )}
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Quality Metrics Table */}
      <GlassCard>
        <GlassCardHeader>
          <GlassCardTitle>Quality Metrics by Tenant</GlassCardTitle>
          <GlassCardDescription>ASR, MOS, and network quality indicators</GlassCardDescription>
        </GlassCardHeader>
        <GlassCardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Tenant</th>
                  <th className="text-right p-3">ASR %</th>
                  <th className="text-right p-3">MOS</th>
                  <th className="text-right p-3">Jitter (ms)</th>
                </tr>
              </thead>
              <tbody>
                {qualityMetrics.map((metric, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50 dark:hover:bg-white/5">
                    <td className="p-3 font-medium">{metric.tenant_name}</td>
                    <td className="p-3 text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        Number(metric.asr_percentage) >= 80 ? 'bg-green-100 text-green-800' :
                        Number(metric.asr_percentage) >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {Number(metric.asr_percentage || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        Number(metric.avg_mos) >= 4.0 ? 'bg-green-100 text-green-800' :
                        Number(metric.avg_mos) >= 3.5 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {metric.avg_mos ? Number(metric.avg_mos).toFixed(2) : 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 text-right">{metric.avg_jitter ? Number(metric.avg_jitter).toFixed(2) : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCardContent>
      </GlassCard>
    </div>
  );
}
